import { useLayoutEffect, useRef, useState } from 'react';

import type { ProcessedNotificationKind } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetProcessedNotifications } from 'cpool-tables/backend-resolvers';

import { NotificationGroup } from './NotificationGroup';
import { ProcessedEntryRow } from './ProcessedEntryRow';
import { cn } from '~/utils/cnTailwind';

type Props = {
    kind: ProcessedNotificationKind;
    entries: SchemaLoanRequestResolver_GetProcessedNotifications;
    onNavigate: () => void;
};

/**
 * One category section of the processed tab. Each row resolves its lead asynchronously and may
 * render nothing (e.g. the adviser no longer has access to the lead). We watch the rows container
 * and collapse the whole group — heading included — whenever every row resolves to empty, so a
 * heading never lingers above an empty list.
 */
export const ProcessedGroup: React.FC<Props> = ({ entries, kind, onNavigate }) => {
    const rowsRef = useRef<HTMLDivElement>(null);
    const [hasRenderedRow, setHasRenderedRow] = useState(false);

    useLayoutEffect(() => {
        const node = rowsRef.current;
        if (!node) return undefined;
        const sync = () => setHasRenderedRow(node.childElementCount > 0);
        sync();
        const observer = new MutationObserver(sync);
        observer.observe(node, { childList: true });
        return () => observer.disconnect();
    }, []);

    return (
        <NotificationGroup kind={kind} rowsRef={rowsRef} className={cn(!hasRenderedRow && 'hidden')}>
            {entries.map((entry) => (
                <ProcessedEntryRow key={`${entry.loanRequestId}-${entry.kind}`} entry={entry} onNavigate={onNavigate} />
            ))}
        </NotificationGroup>
    );
};
